/**
 * Tipos para CGPHAR2Service — Catálogo General del Patrimonio Histórico Andaluz
 *
 * Datos: CGPHA / OBPH (Observatorio del Patrimonio Histórico Andaluz)
 * Incluye: ~18.478 bienes inmuebles de interés histórico
 * CRS: EPSG:25830 (UTM30 ETRS89)
 * Ruta R2: /cgpha/{ine5}.json.gz
 *
 * @module types/cgpha
 * @version 1.0.0
 * @date 2026-02-20
 */

// =============================================================================
// FEATURE — Esquema generado por cgpha-download.ts
// =============================================================================

/** Feature CGPHA almacenada en R2 */
export interface CGPHAFeature {
  /** ID CGPHA interno */
  id: number;
  /** Código CGPHA (ej: "01180230001") */
  codigo: string;
  /** Nombre del bien patrimonial */
  nombre: string;
  /** Tokens pre-normalizados para búsqueda (lowercase, sin tildes) */
  tokens: string;
  /** Coordenada X UTM30 EPSG:25830 */
  x: number;
  /** Coordenada Y UTM30 EPSG:25830 */
  y: number;
  /** Código INE municipio (5 dígitos) */
  cod_ine: string;
  /** Nombre municipio */
  municipio: string;
  /** Provincia */
  provincia: string;
  /** Tipo de bien patrimonial (ej: "BIC", "Catalogado", "Inventariado") */
  tipo: string;
  /** Bien protegido (BIC declarado, etc.) */
  protegido: boolean;
}

// =============================================================================
// OPCIONES DE BÚSQUEDA
// =============================================================================

/** Opciones para buscar en CGPHA R2 */
export interface CGPHASearchOptions {
  /** Código INE del municipio (5 dígitos) */
  codINE: string;
  /** Nombre del bien a buscar */
  name: string;
  /** Confianza mínima para aceptar resultado (0-100) */
  minConfidence?: number;
  /** Máximo de resultados */
  maxResults?: number;
  /** Filtrar solo protegidos (BIC) */
  onlyProtected?: boolean;
}

// =============================================================================
// RESULTADO
// =============================================================================

/** Resultado de búsqueda CGPHA */
export interface CGPHAResult {
  /** Coordenada X UTM30 EPSG:25830 */
  x: number;
  /** Coordenada Y UTM30 EPSG:25830 */
  y: number;
  /** Confianza del match (0-100) */
  confidence: number;
  /** Precisión en metros */
  precision: number;
  /** Fuente */
  source: 'cgpha_r2';
  /** Feature original */
  feature: CGPHAFeature;
  /** Score fuzzy (0-1) si aplica */
  fuzzyScore?: number;
}

// =============================================================================
// CONSTANTES DE VALIDACIÓN
// =============================================================================

/** Rango X válido para coordenadas CGPHA Andalucía (EPSG:25830) */
export const CGPHA_X_RANGE: [number, number] = [100_000, 600_000];

/** Rango Y válido para coordenadas CGPHA Andalucía (EPSG:25830) */
export const CGPHA_Y_RANGE: [number, number] = [3_980_000, 4_290_000];

/** Precisión estimada en metros (coordenadas CGPHA aproximadas) */
export const CGPHA_PRECISION_M = 25;
